import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import { authErrorMessage, resetPassword } from "../service/authService";

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");

    if (!token) {
      setError("This reset link is invalid or incomplete.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ token, password });
      setMessage("Password updated successfully. You can now log in.");
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(authErrorMessage(err, "reset"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100svh",
        px: 2.5,
        pt: { xs: 13, md: 16 },
        pb: 8,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(circle at 20% 15%, rgba(220,203,182,.35), transparent 34%), linear-gradient(135deg,#f7f3ec,#eee5d8)",
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 460,
          borderRadius: 4,
          boxShadow: "0 28px 70px rgba(35,45,37,.14)",
          border: "1px solid rgba(24,36,29,.1)",
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" fontWeight={900} sx={{ mb: 1 }}>
            Reset password
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>
            Choose a new password for your Rustic World account.
          </Typography>

          {message && <Alert severity="success" role="status" sx={{ mb: 2, fontSize: 16 }}>{message}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {message ? <Button variant="contained" fullWidth onClick={() => navigate("/login", { replace: true })} sx={{ minHeight: 56, fontSize: 17 }}>Sign in now</Button> : <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="New password"
              type="password"
              fullWidth
              margin="normal"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ "& .MuiInputBase-root": { minHeight: 56, fontSize: 18 } }}
            />
            <TextField
              label="Confirm password"
              type="password"
              fullWidth
              margin="normal"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ "& .MuiInputBase-root": { minHeight: 56, fontSize: 18 } }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={loading}
              sx={{ mt: 2, minHeight: 56, fontSize: 17, fontWeight: 800 }}
            >
              {loading ? "Updating..." : "Update password"}
            </Button>
            <Button
              type="button"
              variant="text"
              fullWidth
              onClick={() => navigate("/login")}
              sx={{ mt: 1, minHeight: 52, fontSize: 16, fontWeight: 700 }}
            >
              Back to login
            </Button>
          </Box>}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPasswordPage;
